import gsap from 'gsap/gsap-core';
import * as PIXI from 'pixi.js'
// import koko from '../koko'
const SPECIAL_KEYS = [
    'DEL',
    'SPACE',
    'SPC',
    'BKSPC',
    'CLOSE',
    'NUM',
    'SYM',
    'CAP',
    'LOW',
    'NEXT',
    'PREV',
    'CLEAR',
]

const CLICK_CHECK = {canClick: true};
function setupClicker(buttonDisplayObj, clickHandler) {
    let f = () => {
        // console.log('click function');
        if (CLICK_CHECK.canClick) {
            // console.log('click accepted');
            CLICK_CHECK.canClick = false;
            setTimeout(enableClick, 20);
            clickHandler();
        }
    }
    buttonDisplayObj.interactive = true;
    buttonDisplayObj.on('pointerdown', f);
    buttonDisplayObj.on('mousedown', f);
    buttonDisplayObj.on('touchstart', f);
}

function enableClick() {
    // console.log('enabling click');
    CLICK_CHECK.canClick = true;
}

export class SimpleCustomKeyboard {
    // Images are used if specified otherwise we use the colour specified
    setup = {
        // Support multi-layouts for upper-case / lower-case / symbols / numbers etc.
        // It's up to you to switch, but the simplest way is to create a key [Num], [Sym], [Cap], [Low] 
        // then add props for buttonLabelNum: '123', buttonLabelSym: '@#!', buttonLabelCap: 'ABC', buttonLabelLow: 'abc',
        // you can then add more props for these:
        //
        //      buttonBGImgIdDel: null,
        //      buttonBGImgDelWidth: 0,
        //      buttonBGImgDelHeight: 0,
        //      hideDelText: false,
        //
        // just replace Del with Num, Sym, Cap, Low and these props will be applied to these keys.
        // Then in your keyCallback you can check for strings 'Num', 'Sym', 'Cap', 'Low' and call
        // switchLayout(layoutIndex) with the correct index in the layouts array.
        // If you want more than 4 layouts you will need to either control layout switching outside of the keyboard,
        // or add more special keys to the SPECIAL_KEYS const above or use Next / Prev instead for switching.

        layouts: [
            ['ABCDEFGHIJKLM[Del]',
             'NOPQRSTUVWXYZ[Spc]'],
        ],

        font: 'typrighter_small',
        fontSize: 30,
        fontColor: 0xffffff,
        fontXOffset: 0,
        fontYOffset: 0,

        minButtonWidth: 20,
        minButtonHeight: 20,
        autoSizeButtons: true,
        fixedSizeButtons: false,

        // image ids are texture ids
        buttonBGImgId: null,
        buttonBGImgWidth: 0,
        buttonBGImgHeight: 0,
        hideText: false,

        buttonBGImgIdDel: null,
        buttonBGImgDelWidth: 0,
        buttonBGImgDelHeight: 0,
        hideDelText: false,
        buttonLabelDel: 'Del',

        buttonBGImgIdBkspc: null,
        buttonBGImgBkspcWidth: 0,
        buttonBGImgBkspcHeight: 0,
        buttonLabelBkspc: 'BkSpc',
        hideBkSpcText: false,

        buttonBGImgIdSpace: null,
        buttonBGImgSpaceWidth: 0,
        buttonBGImgSpaceHeight: 0,
        buttonLabelSpace: 'Spc',
        hideSpaceText: false,

        // You can put a close button in the same way as any other button,
        // just label it '[CLOSE]' in your layout and set showCloseButton to false.
        // If you set showCloseButton to true a specific close button will be added to the top left.
        showCloseButton: true,
        hideCloseText: false,
        buttonBGImgIdClose: null,
        buttonBGImgCloseWidth: 0,
        buttonBGImgCloseHeight: 0,
        buttonLabelClose: 'X',
        closeButtonBGColor: 0xAA0000,
        closeFont: 'typrighter_small',
        closeFontSize: 20,
        closeFontColor: 0xFFFFFF,

        buttonBGColor: 0,
        buttonBGAlpha: 1,

        mainBGImgId: null,
        mainBGWidth: 0,
        mainBGHeight: 0,
        mainBGColor: 0xFFFFFF,
        mainBGAlpha: 0.9,
        autoSizeBG: true,

        title: 'Type Your Answer',
        titleFont: 'typrighter_small',
        titleFontSize: 30,
        titleFontColor: 0,
        showTitle: true,

        edgePadding: 10,
        betweenPadding: 5,
        rowSpacing: 10,
        buttonInnerPadding: 3,
        buttonInnerPaddingTB: 0,
        buttonInnerPaddingLR: 8,

        showKeyTapHighlight: true,
        keyTapHighlightColor: 0xaa0000,
        keyTapHighlightAlpha: 0.6,
        keyTapHighlightFadeTime: 0.4,
        keyTapHighlightFadeDelay: 0.1,
        keyTapHighlightPadding: 1,

        keyHighlightColour: 0x333388,
        keyHighlightAlpha: 0.6,
        keyHighlightPadding: 1,
    };

    keyCallback = null;
    container = null;
    keyboardContainer = null;
    children = null;
    keys = null;
    currentLayout = 0;
    keyboardOpen = false;

    constructor(setup = null) {
        if (setup) {
            for (let p in setup) {
                this.setup[p] = setup[p];
            }
            // We'll have 2 variations on space, please!
            this.setup.buttonBGImgIdSpc = this.setup.buttonBGImgIdSpace;
            this.setup.buttonBGImgSpcWidth = this.setup.buttonBGImgSpaceWidth;
            this.setup.buttonBGImgSpcHeight = this.setup.buttonBGImgSpaceHeight;
            this.setup.buttonLabelSpc = this.setup.buttonLabelSpace;
            this.setup.hideSpcText = this.setup.hideSpaceText;
        }
    }

    // x, y is the top / middle of the keyboard
    createKeyboard = (x, y, container, keyCallback = null, layout = 0) => {
        let setup = this.setup;

        this.currentLayout = layout;
        setup.layout = setup.layouts[layout];

        this.keyCallback = keyCallback;

        this.children = [];
        this.keys = [];
        this.container = container;
        this.keyboardContainer = new PIXI.Container();
        this.keyboardContainer.x = x;
        this.keyboardContainer.y = y;
        this.container.addChild(this.keyboardContainer);
        this.children.kbcontainer = this.keyboardContainer;

        let bg = setup.mainBGImgId ? PIXI.Sprite.from(setup.mainBGImgId) : new PIXI.Graphics();
        if (setup.mainBGImgId == null) {
            bg.beginFill(setup.mainBGColor || '#000000');
            // we'll fix the size later
            bg.drawRect(0, 0, 50, 50);
            bg.endFill();
            bg.alpha = setup.mainBGAlpha;
        } else {
            if (setup.mainBGWidth > 0) {
                bg.width = setup.mainBGWidth;
            }
            if (setup.mainBGHeight > 0) {
                bg.height = setup.mainBGHeight;
            }
        }
        this.keyboardContainer.addChild(bg);
        this.children.bg = bg;

        let contentsContainer = new PIXI.Container();
        contentsContainer.x = 0;
        contentsContainer.y = setup.edgePadding;
        this.keyboardContainer.addChild(contentsContainer);
        this.children.contentsContainer = contentsContainer;

        let rowY = 0;

        if (setup.title && setup.title !== '' && setup.showTitle === true) {
            let style = { fontName: setup.titleFont, fontSize: setup.titleFontSize || 30, tint: setup.titleFontColor || 0, align: 'center' };
            let titleText = new PIXI.BitmapText(setup.title, style);
            titleText.x = 0;
            titleText.y = rowY;
            titleText.anchor.x = 0.5;
            titleText.anchor.y = 0;
            contentsContainer.addChild(titleText);
            rowY = setup.rowSpacing > 0 ? titleText.textHeight + setup.rowSpacing : titleText.textHeight + setup.betweenPadding;
        }

        let keystyle = { fontName: setup.font, fontSize: setup.fontSize || 30, tint: setup.fontColor || 0, align: 'center' };
        for (let r = 0; r < setup.layout.length; r++) {
            // square brackets are used around space / delete and other special keys
            let keySplit1 = setup.layout[r].split('[');
            let keysToAdd = [];
            for (let i = 0; i < keySplit1.length; i++) {
                if (SPECIAL_KEYS.indexOf(keySplit1[i].substr(0, keySplit1[i].length - 1).toUpperCase()) >= 0 || keySplit1[i].substr(keySplit1[i].length - 1, 1) === ']') {
                    // special key!
                    keysToAdd.push(keySplit1[i].substr(0, keySplit1[i].length - 1));
                } else {
                    keysToAdd = keysToAdd.concat(keySplit1[i].split(''));
                }
            }
            // now we have our row of keys split into an array!

            let keyRow = new PIXI.Container();
            this.children['keyrow' + r] = keyRow;
            let keyOffset = 0;
            for (let i = 0; i < keysToAdd.length; i++) {
                let key = new PIXI.Container();
                this.children['keys' + keysToAdd[i]] = key;

                let extraKeyPropId = keysToAdd[i].length === 1 ? '' : keysToAdd[i];
                if (extraKeyPropId !== '') {
                    // Not a default key, but if we have no settings for it, we need them!
                    if (typeof setup['buttonBGImgId' + extraKeyPropId] === 'undefined') {
                        setup['buttonBGImgId' + extraKeyPropId] = setup['buttonBGImgId'];
                    }
                    if (typeof setup['buttonBGImg' + extraKeyPropId + 'Width'] === 'undefined') {
                        setup['buttonBGImg' + extraKeyPropId + 'Width'] = setup['buttonBGImgWidth'];
                    }
                    if (typeof setup['buttonBGImg' + extraKeyPropId + 'Height'] === 'undefined') {
                        setup['buttonBGImg' + extraKeyPropId + 'Height'] = setup['buttonBGImgHeight'];
                    }
                    if (typeof setup['hide' + extraKeyPropId + 'Text'] === 'undefined') {
                        setup['hide' + extraKeyPropId + 'Text'] = setup['hideText'];
                    }
                    if (typeof setup['buttonLabel' + extraKeyPropId] === 'undefined') {
                        setup['buttonLabel' + extraKeyPropId] = keysToAdd[i];
                    }
                }

                let keybg = setup['buttonBGImgId' + extraKeyPropId] ? PIXI.Sprite.from(setup['buttonBGImgId' + extraKeyPropId]) : new PIXI.Graphics();
                if (setup['buttonBGImgId' + extraKeyPropId] == null) {
                    keybg.beginFill(setup.buttonBGColor || '#000000');
                    // we'll fix the size later
                    keybg.drawRect(0, 0, setup.minButtonWidth, setup.minButtonHeight);
                    keybg.endFill();
                    keybg.alpha = setup.buttonBGAlpha;
                } else {
                    keybg.anchor.x = keybg.anchor.y = 0.5;
                    if (setup['buttonBGImg' + extraKeyPropId + 'Width'] > 0) {
                        keybg.width = setup['buttonBGImgId' + extraKeyPropId + 'Width'];
                    }
                    if (setup['buttonBGImg' + extraKeyPropId + 'Width'] > 0) {
                        keybg.height = setup['buttonBGImgId' + extraKeyPropId + 'Width'];
                    }
                }
                key.addChild(keybg);
                this.children['keybg' + keysToAdd[i]] = keybg;

                let highlight = new PIXI.Graphics();
                highlight.beginFill(setup.keyHighlightColour);
                highlight.drawRect(0, 0, setup.minButtonWidth, setup.minButtonHeight);
                highlight.endFill();
                highlight.alpha = setup.keyHighlightAlpha;
                highlight.visible = false;
                key.highlight = highlight;
                key.addChild(highlight);
                this.children['keyhighlight' + keysToAdd[i]] = highlight;

                if (setup.showKeyTapHighlight) {
                    let tapHighlight = new PIXI.Graphics();
                    tapHighlight.beginFill(setup.keyTapHighlightColor);
                    tapHighlight.drawRect(0, 0, setup.minButtonWidth, setup.minButtonHeight);
                    tapHighlight.endFill();
                    tapHighlight.alpha = 0;
                    key.tapHighlight = tapHighlight;
                    key.addChild(tapHighlight);
                    this.children['taphighlight' + keysToAdd[i]] = tapHighlight;
                }

                let keyText = new PIXI.BitmapText(keysToAdd[i], keystyle);
                keyText.anchor.x = 0.5;
                keyText.anchor.y = 0.5;
                keyText.x = setup.fontXOffset;
                keyText.y = setup.fontYOffset;
                key.addChild(keyText);
                this.children['keytext' + keysToAdd[i]] = keyText;
                if (setup['hide' + extraKeyPropId + 'Text'] === true || setup.hideText === true) {
                    keyText.visible = false;
                }
                if (typeof setup['buttonLabel'] + extraKeyPropId === 'string' && setup['buttonLabel'] + extraKeyPropId !== '') {
                    keyText.text = setup['buttonLabel'] + extraKeyPropId;
                }

                if (setup.fixedSizeButtons) {
                    let bWidth = setup['buttonBGImg' + extraKeyPropId + 'Width'] || setup.minButtonWidth;
                    let bHeight = setup['buttonBGImg' + extraKeyPropId + 'Height'] || setup.minButtonHeight;
                    keybg.width = bWidth;
                    keybg.height = bHeight;
                }
                if (setup.autoSizeButtons) {
                    let bWidth = setup.buttonInnerPaddingLR > 0 ? keyText.textWidth + setup.buttonInnerPaddingLR * 2 : keyText.textWidth + setup.buttonInnerPadding * 2;
                    let bHeight = setup.buttonInnerPaddingTB > 0 ? keyText.textHeight + setup.buttonInnerPaddingTB * 2 : keyText.textHeight + setup.buttonInnerPadding * 2;
                    if (bWidth < setup.minButtonWidth) {
                        bWidth = setup.minButtonWidth;
                    }
                    if (bHeight < setup.minButtonHeight) {
                        bHeight = setup.minButtonHeight;
                    }
                    keybg.width = bWidth;
                    keybg.height = bHeight;
                }
                if (setup['buttonBGImgId' + extraKeyPropId] == null) {
                    keybg.x = -keybg.width / 2;
                    keybg.y = -keybg.height / 2;
                }

                key.highlight.width = keybg.width - setup.keyHighlightPadding * 2;
                key.highlight.height = keybg.height - setup.keyHighlightPadding * 2;
                key.highlight.x = -key.highlight.width / 2;
                key.highlight.y = -key.highlight.height / 2;

                if (key.tapHighlight) {
                    key.tapHighlight.width = keybg.width - setup.keyTapHighlightPadding * 2;
                    key.tapHighlight.height = keybg.height - setup.keyTapHighlightPadding * 2;
                    key.tapHighlight.x = -key.tapHighlight.width / 2;
                    key.tapHighlight.y = -key.tapHighlight.height / 2;
                }

                key.x = keyOffset + key.width / 2;
                key.y = key.height / 2;
                keyOffset += key.width + setup.betweenPadding;
                keyRow.addChild(key);

                // handle taps...
                if (this.keyCallback) {
                    let kId = keysToAdd[i];
                    /*
                    key.interactive = true;
                    let event = koko.client.system.isMobile ? 'touchstart' : 'mousedown';
                    key.on(event, (e) => {
                        this.highlightKeyTapped(key);
                        this.keyCallback(kId, e);
                    });
                    */
                    setupClicker(key, (e) => {
                        this.highlightKeyTapped(key);
                        this.keyCallback(kId, e);
                    });
                }

                key.letter = keysToAdd[i];
                this.keys.push(key);
            }

            keyRow.x = -keyRow.width / 2;
            keyRow.y = rowY;
            contentsContainer.addChild(keyRow);
            rowY += setup.rowSpacing > 0 ? keyRow.height + setup.rowSpacing : keyRow.height + setup.betweenPadding;
        }
        if (setup.showCloseButton) {
            let key = new PIXI.Container();
            this.children.closekey = key;
            let keybg = setup.buttonBGImgIdClose ? PIXI.Sprite.from(setup.buttonBGImgIdClose) : new PIXI.Graphics();
            if (setup.buttonBGImgIdClose == null) {
                keybg.beginFill(setup.closeButtonBGColor || '#000000');
                // we'll fix the size later
                keybg.drawRect(0, 0, setup.minButtonWidth, setup.minButtonHeight);
                keybg.endFill();
                keybg.alpha = setup.buttonBGAlpha;
            } else {
                keybg.anchor.x = keybg.anchor.y = 0.5;
                if (setup.closeBGWidth > 0) {
                    bg.width = setup.closeBGWidth;
                }
                if (setup.closeBGHeight > 0) {
                    bg.height = setup.closeBGHeight;
                }
            }
            key.addChild(keybg);
            this.children.closebg = keybg;

            let highlight = new PIXI.Graphics();
            highlight.beginFill(setup.keyHighlightColour);
            highlight.drawRect(0, 0, setup.minButtonWidth, setup.minButtonHeight);
            highlight.endFill();
            highlight.alpha = setup.keyHighlightAlpha;
            highlight.visible = false;
            key.highlight = highlight;
            key.addChild(highlight);
            this.children.keyhighlightclose = highlight;

            if (setup.showKeyTapHighlight) {
                let tapHighlight = new PIXI.Graphics();
                tapHighlight.beginFill(setup.keyTapHighlightColor);
                tapHighlight.drawRect(0, 0, setup.minButtonWidth, setup.minButtonHeight);
                tapHighlight.endFill();
                tapHighlight.alpha = 0;
                key.tapHighlight = tapHighlight;
                key.addChild(tapHighlight);
                this.children.taphighlightclose = tapHighlight;
            }

            let closekeystyle = { fontName: setup.closeFont, fontSize: setup.closeFontSize || 30, tint: setup.closeFontColor || 0, align: 'center' };
            let keyText = new PIXI.BitmapText('X', closekeystyle);
            keyText.anchor.x = 0.5;
            keyText.anchor.y = 0.5;
            keyText.x = setup.fontXOffset;
            keyText.y = setup.fontYOffset;
            key.addChild(keyText);
            this.children.closetext = keyText;
            if (setup.hideCloseText === true) {
                keyText.visible = false;
            }

            if (setup.fixedSizeButtons) {
                let bWidth = setup.buttonBGImgCloseWidth || setup.minButtonWidth;
                let bHeight = setup.buttonBGImgCloseHeight || setup.minButtonHeight;
                keybg.width = bWidth;
                keybg.height = bHeight;
            }
            if (setup.autoSizeButtons) {
                let bWidth = setup.buttonInnerPaddingLR > 0 ? keyText.textWidth + setup.buttonInnerPaddingLR * 2 : keyText.textWidth + setup.buttonInnerPadding * 2;
                let bHeight = setup.buttonInnerPaddingTB > 0 ? keyText.textHeight + setup.buttonInnerPaddingTB * 2 : keyText.textHeight + setup.buttonInnerPadding * 2;
                if (bWidth < setup.minButtonWidth) {
                    bWidth = setup.minButtonWidth;
                }
                if (bHeight < setup.minButtonHeight) {
                    bHeight = setup.minButtonHeight;
                }
                keybg.width = bWidth;
                keybg.height = bHeight;
            }
            keybg.x = -keybg.width / 2;
            keybg.y = -keybg.height / 2;

            key.highlight.width = keybg.width - setup.keyHighlightPadding * 2;
            key.highlight.height = keybg.height - setup.keyHighlightPadding * 2;
            key.highlight.x = -key.highlight.width / 2;
            key.highlight.y = -key.highlight.height / 2;

            if (key.tapHighlight) {
                key.tapHighlight.width = keybg.width - setup.keyTapHighlightPadding * 2;
                key.tapHighlight.height = keybg.height - setup.keyTapHighlightPadding * 2;
                key.tapHighlight.x = -key.tapHighlight.width / 2;
                key.tapHighlight.y = -key.tapHighlight.height / 2;
            }

            key.x = -contentsContainer.width / 2 + key.width / 2;
            key.y = key.height / 2;
            contentsContainer.addChild(key);

            if (this.keyCallback) {
                /*
                key.interactive = true;
                let event = koko.client.system.isMobile ? 'touchstart' : 'mousedown';
                key.on(event, (e) => {
                    this.highlightKeyTapped(key);
                    this.keyCallback('close', e);
                });
                */
                setupClicker(key, (e) => {
                    this.highlightKeyTapped(key);
                    this.keyCallback('close', e);
                });
            }

            key.letter = 'close';
            this.keys.push(key);
        }
        if (setup.autoSizeBG) {
            bg.width = contentsContainer.width + setup.edgePadding * 2;
            bg.height = contentsContainer.height + setup.edgePadding * 2;
        }
        bg.y = 0;
        bg.x = -bg.width / 2;

        this.keyboardOpen = true;
        return this.keyboardContainer;
    }

    highlightKeyTapped = (key) => {
        if (key.tapHighlight) {
            key.tapHighlight.alpha = this.setup.keyTapHighlightAlpha;
            gsap.killTweensOf(key.tapHighlight);
            gsap.to(key.tapHighlight, {alpha: 0, delay: this.setup.keyTapHighlightFadeDelay, duration: this.setup.keyTapHighlightFadeTime});
        }
    }

    highlightKeys = (keysArray) => {
        // console.log(keysArray, this.keys);
        if (this.keys) {
            for (let i = 0; i < this.keys.length; i++) {
                if (keysArray.indexOf(this.keys[i].letter) >= 0) {
                    this.keys[i].highlight.visible = true;
                } else {
                    this.keys[i].highlight.visible = false;
                }
            }
        }
    }

    switchLayout = (layout) => {
        if (this.keyboardOpen) {
            if (layout >= 0 && this.currentLayout !== layout && this.setup.layouts.length > layout) {
                let x = this.keyboardContainer.x;
                let y = this.keyboardContainer.y;
                let container = this.container;
                let keyCallback = this.keyCallback;
                this.removeKeyboard();
                return this.createKeyboard(x, y, container, keyCallback, layout);
            }
        }
    }

    removeKeyboard = () => {
        if (this.children) {
            for (let p in this.children) {
                gsap.killTweensOf(this.children[p]);
                this.children[p].parent.removeChild(this.children[p]);
            }
            for (let p in this.children) {
                this.children[p].destroy();
            }
            this.children = null;
            this.keys = null;
            this.keyboardContainer = null;
            this.container = null;
            this.keyCallback = null;
            this.keyboardOpen = false;
        }
    }
}